<!-- eslint-disable max-len -->
<template>
  <b-modal
    v-model="showModal"
    title="Create New Goal"
    size="xl"
    centered
    hide-footer
    no-close-on-backdrop
    @hidden="handleModalHidden"
  >
    <div class="goal-wizard">
      <!-- Progress Indicator -->
      <div class="wizard-progress">
        <div class="progress-steps">
          <div
            v-for="stepNum in totalSteps"
            :key="stepNum"
            class="step"
            :class="{
              'completed': stepNum < currentStep,
              'active': stepNum === currentStep
            }"
          >
            <span class="step-number">{{ stepNum }}</span>
            <span class="step-title">{{ getStepTitle(stepNum) }}</span>
          </div>
        </div>
      </div>

      <!-- Step Content -->
      <div class="wizard-content">
        <!-- Step 1: Goal Type Selection -->
        <div v-if="currentStep === 1" class="wizard-step">
          <h3 class="step-heading cabin-family semi-bold">What would you like to save for?</h3>

          <div v-if="loadingGoalTypes" class="text-center">
            <LoadingSpinner text="Loading goal types..." />
          </div>

          <div v-else class="goal-types-container">
            <div
              v-for="category in goalCategories"
              :key="category"
              class="category-section"
            >
              <h4 class="category-title cabin-family semi-bold">
                {{ formatCategoryName(category) }}
              </h4>

              <div class="goal-types-grid">
                <div
                  v-for="type in goalTypesByCategory[category]"
                  :key="type.id"
                  class="goal-type-card"
                  :class="{ 'selected': selectedGoalType && selectedGoalType.id === type.id }"
                  @click="selectGoalType(type)"
                >
                  <div class="type-icon">
                    <i :class="'fas ' + getIconClass(type)"></i>
                  </div>
                  <h5 class="type-name cabin-family semi-bold">{{ type.name }}</h5>
                  <p class="type-description">{{ type.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2: Goal Configuration -->
        <div v-if="currentStep === 2" class="wizard-step">
          <div class="step-header">
            <button type="button" class="btn btn-link back-button" @click="previousStep">
              <i class="fas fa-arrow-left"></i> Back
            </button>
            <h3 class="step-heading cabin-family semi-bold">
              Configure Your {{ selectedGoalType ? selectedGoalType.name : 'Goal' }}
            </h3>
          </div>

          <form @submit.prevent="nextStep">
            <div class="form-grid">
              <div class="form-group">
                <label for="title" class="form-label cabin-family semi-bold">Goal Name *</label>
                <input
                  v-model="form.title"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.title }"
                  placeholder="e.g., Emergency Fund - 6 months"
                />
                <div v-if="errors.title" class="error-message">{{ errors.title }}</div>
              </div>

              <div class="form-group">
                <label for="description" class="form-label cabin-family semi-bold">Description (Optional)</label>
                <textarea
                  v-model="form.description"
                  class="form-control"
                  rows="3"
                  placeholder="Describe your goal..."
                />
              </div>

              <div class="form-group">
                <label for="targetAmount" class="form-label cabin-family semi-bold">Target Amount *
                </label>
                <CurrencyInput
                  v-model="form.targetAmount"
                  :has-error="!!errors.targetAmount"
                  :error-message="errors.targetAmount"
                  placeholder="Enter your target amount"
                />
              </div>

              <div class="form-group">
                <label for="targetDate" class="form-label cabin-family semi-bold">Target Date *</label>
                <DatePicker
                  v-model="form.targetDate"
                  :has-error="!!errors.targetDate"
                  :error-message="errors.targetDate"
                  :min-date="new Date().toISOString().split('T')[0]"
                />
              </div>

              <div class="form-group">
                <label for="frequency" class="form-label cabin-family semi-bold">Contribution Frequency *</label>
                <select
                  v-model="form.frequency"
                  class="form-control"
                  :class="{ 'is-invalid': errors.frequency }"
                >
                  <option value="weekly">Weekly</option>
                  <option value="bi_weekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
                <div v-if="errors.frequency" class="error-message">{{ errors.frequency }}</div>
              </div>

              <div class="form-group">
                <label for="frequencyAmount" class="form-label cabin-family semi-bold">Contribution Amount *</label>
                <CurrencyInput
                  v-model="form.frequencyAmount"
                  :has-error="!!errors.frequencyAmount"
                  :error-message="errors.frequencyAmount"
                  placeholder="Enter contribution amount"
                />
              </div>

              <div class="form-group">
                <label for="priority" class="form-label cabin-family semi-bold">Priority Level</label>
                <select v-model="form.priority" class="form-control">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div class="form-group checkbox-group">
                <div class="form-check">
                  <input
                    v-model="form.autoDebit"
                    type="checkbox"
                    class="form-check-input"
                    id="autoDebit"
                  />
                  <label for="autoDebit" class="form-check-label">
                    Enable automatic contributions from wallet
                  </label>
                </div>
                <p class="help-text secondary-text">
                  Your wallet will be automatically debited on the scheduled dates
                </p>
              </div>
            </div>
          </form>
        </div>

        <!-- Step 3: Review and Confirm -->
        <div v-if="currentStep === 3" class="wizard-step">
          <div class="step-header">
            <button type="button" class="btn btn-link back-button" @click="previousStep">
              <i class="fas fa-arrow-left"></i> Back
            </button>
            <h3 class="step-heading cabin-family semi-bold">Review Your Goal</h3>
          </div>

          <div class="goal-review">
            <div class="review-card">
              <div class="goal-summary">
                <div class="goal-header-review">
                  <div class="goal-icon-large">
                    <i :class="'fas ' + getIconClass(selectedGoalType)"></i>
                  </div>
                  <div class="goal-info-review">
                    <h4 class="cabin-family semi-bold">{{ form.title }}</h4>
                    <p class="secondary-text">{{ selectedGoalType ? selectedGoalType.name : 'Goal' }}</p>
                  </div>
                </div>

                <p v-if="form.description" class="goal-description">{{ form.description }}</p>
              </div>

              <div class="goal-details-grid">
                <div class="detail-item">
                  <span class="label">Target Amount</span>
                  <span class="value primary cabin-family semi-bold">{{ formatCurrency(form.targetAmount) }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Target Date</span>
                  <span class="value">{{ formatDate(form.targetDate) }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Contribution</span>
                  <span class="value">{{ formatCurrency(form.frequencyAmount) }} {{ form.frequency }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Priority</span>
                  <span class="value" :class="`priority-${form.priority}`">{{ form.priority.toUpperCase() }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Auto Debit</span>
                  <span class="value">{{ form.autoDebit ? 'Enabled' : 'Disabled' }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Duration</span>
                  <span class="value">{{ calculateDuration() }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Wizard Actions -->
      <div class="wizard-actions">
        <button
          v-if="currentStep > 1"
          type="button"
          class="btn btn-outline-secondary"
          @click="previousStep"
        >
          Back
        </button>

        <button
          type="button"
          class="btn btn-outline-secondary"
          @click="closeWizard"
        >
          Cancel
        </button>

        <button
          v-if="currentStep < totalSteps"
          type="button"
          class="btn primary-btn"
          :disabled="!canProceedToNext()"
          @click="nextStep"
        >
          Next
        </button>

        <button
          v-else
          type="button"
          class="btn primary-btn"
          :disabled="loading"
          @click="submitGoal"
        >
          <LoadingSpinner v-if="loading" size="small" variant="white" />
          <span v-else>Create Goal</span>
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { formatCurrency, formatDate, daysBetween } from '../../utils/formatters';
import { CurrencyInput, DatePicker, LoadingSpinner } from '../shared';

export default {
  name: 'GoalCreationWizard',
  components: {
    CurrencyInput,
    DatePicker,
    LoadingSpinner,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showModal: false,
      currentStep: 1,
      totalSteps: 3,
      loading: false,
      loadingGoalTypes: false,
      selectedGoalType: null,
      form: {
        title: '',
        description: '',
        targetAmount: '',
        targetDate: '',
        frequency: 'monthly',
        frequencyAmount: '',
        priority: 'medium',
        autoDebit: false,
      },
      errors: {},
    };
  },
  computed: {
    ...mapState('goals', ['goalTypes']),

    goalCategories() {
      return [...new Set(this.goalTypes.map((type) => type.category))];
    },

    goalTypesByCategory() {
      const grouped = {};
      this.goalCategories.forEach((category) => {
        grouped[category] = this.goalTypes.filter((type) => type.category === category);
      });
      return grouped;
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(newVal) {
        this.showModal = newVal;
        if (newVal && this.goalTypes.length === 0) {
          this.loadGoalTypes();
        }
      },
    },
  },
  methods: {
    ...mapActions('goals', ['fetchGoalTypes', 'createGoal']),

    formatCurrency,
    formatDate,

    async loadGoalTypes() {
      this.loadingGoalTypes = true;
      try {
        await this.fetchGoalTypes();
      } catch (error) {
        console.error('Error loading goal types:', error);
        this.$toast.error('Failed to load goal types');
      } finally {
        this.loadingGoalTypes = false;
      }
    },

    formatCategoryName(category) {
      return category.replace('_', ' ').toUpperCase();
    },

    getStepTitle(stepNum) {
      const titles = {
        1: 'Choose Type',
        2: 'Configure',
        3: 'Review',
      };
      return titles[stepNum] || '';
    },

    selectGoalType(type) {
      this.selectedGoalType = type;
    },

    canProceedToNext() {
      switch (this.currentStep) {
        case 1:
          return this.selectedGoalType !== null;
        case 2:
          return this.isStep2Valid();
        default:
          return true;
      }
    },

    isStep2Valid() {
      return this.form.title.trim()
        && this.form.targetAmount && this.form.targetAmount > 0
        && this.form.targetDate
        && this.form.frequencyAmount && this.form.frequencyAmount > 0;
    },

    validateStep2() {
      this.errors = {};

      if (!this.form.title.trim()) {
        this.$set(this.errors, 'title', 'Goal title is required');
      }

      if (!this.form.targetAmount || this.form.targetAmount <= 0) {
        this.$set(this.errors, 'targetAmount', 'Target amount must be greater than 0');
      }

      if (!this.form.targetDate) {
        this.$set(this.errors, 'targetDate', 'Target date is required');
      }

      if (!this.form.frequencyAmount || this.form.frequencyAmount <= 0) {
        this.$set(this.errors, 'frequencyAmount', 'Contribution amount must be greater than 0');
      }

      return Object.keys(this.errors).length === 0;
    },

    nextStep() {
      if (this.currentStep === 2) {
        // Validate step 2 and show errors if invalid
        if (!this.validateStep2()) {
          return; // Don't proceed if validation fails
        }
      }

      if (this.canProceedToNext()) {
        this.currentStep += 1;
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep -= 1;
      }
    },

    calculateDuration() {
      if (!this.form.targetDate) return 'N/A';

      const today = new Date();
      const targetDate = new Date(this.form.targetDate);
      const days = daysBetween(today, targetDate);

      if (days <= 30) return `${days} days`;
      if (days <= 365) return `${Math.round(days / 30)} months`;
      return `${Math.round(days / 365)} years`;
    },

    async submitGoal() {
      this.loading = true;

      try {
        const goalData = {
          ...this.form,
          goalTypeId: this.selectedGoalType.id,
          target_amount: parseFloat(this.form.targetAmount),
          frequency_amount: parseFloat(this.form.frequencyAmount),
        };

        const result = await this.createGoal(goalData);

        if (result.success) {
          this.$toast.success('Goal created successfully!');
          this.$emit('goal-created', result.data.goal);
          this.closeWizard();
        } else {
          this.$toast.error(result.error || 'Failed to create goal');
        }
      } catch (error) {
        console.error('Goal creation error:', error);
        this.$toast.error('An error occurred while creating the goal');
      } finally {
        this.loading = false;
      }
    },

    getIconClass(goalType) {
      const category = goalType && (goalType.icon || goalType.category);
      const map = {
        savings: 'fa-piggy-bank',
        debt_reduction: 'fa-credit-card',
        investment: 'fa-chart-line',
        emergency_fund: 'fa-shield-halved',
        education: 'fa-graduation-cap',
        business: 'fa-briefcase',
        vacation: 'fa-plane',
        home: 'fa-house',
        car: 'fa-car',
        retirement: 'fa-umbrella-beach',
      };
      return map[category] || 'fa-bullseye';
    },

    resetWizard() {
      this.currentStep = 1;
      this.selectedGoalType = null;
      this.form = {
        title: '',
        description: '',
        targetAmount: '',
        targetDate: '',
        frequency: 'monthly',
        frequencyAmount: '',
        priority: 'medium',
        autoDebit: false,
      };
      this.errors = {};
      this.loading = false;
    },

    closeWizard() {
      this.showModal = false;
      this.$emit('update:visible', false);
    },

    handleModalHidden() {
      this.resetWizard();
      this.$emit('update:visible', false);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../scss/variable.scss';

.goal-wizard {
  .wizard-progress {
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #E0E0E0;
  }

  .progress-steps {
    display: flex;
    justify-content: space-between;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 15px;
      left: 15px;
      right: 15px;
      height: 2px;
      background: #E0E0E0;
      z-index: 1;
    }
  }

  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 2;

    &.completed .step-number {
      background: $success;
      color: white;
    }

    &.active .step-number {
      background: $primary;
      color: white;
    }
  }

  .step-number {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #E0E0E0;
    color: $secondary;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 0.5rem;
  }

  .step-title {
    font-size: 12px;
    color: $secondary;
    text-align: center;
  }

  .wizard-content {
    min-height: 400px;
    margin-bottom: 2rem;
  }

  .step-heading {
    color: $black;
    margin-bottom: 1.5rem;
    text-align: center;
  }

  .step-header {
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;

    .back-button {
      color: $primary;
      text-decoration: none;
      margin-right: 1rem;

      &:hover {
        color: darken($primary, 10%);
      }
    }

    .step-heading {
      margin-bottom: 0;
      text-align: left;
    }
  }

  .goal-types-container {
    max-height: 400px;
    overflow-y: auto;
  }

  .category-section {
    margin-bottom: 2rem;
  }

  .category-title {
    color: $black;
    margin-bottom: 1rem;
    font-size: 16px;
  }

  .goal-types-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .goal-type-card {
    background: $white;
    border: 2px solid #E0E0E0;
    border-radius: 8px;
    padding: 1rem;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: $primary;
      transform: translateY(-2px);
    }

    &.selected {
      border-color: $primary;
      background: $primary-light;
    }
  }

  .type-icon {
    width: 48px;
    height: 48px;
    margin: 0 auto 0.75rem;
    border-radius: 50%;
    background: $primary-light;
    display: flex;
    align-items: center;
    justify-content: center;

    .icon-image {
      width: 24px;
      height: 24px;
    }

    i {
      font-size: 20px;
      color: $primary;
    }
  }

  .type-name {
    margin-bottom: 0.5rem;
    color: $black;
    font-size: 14px;
  }

  .type-description {
    font-size: 12px;
    color: $secondary;
    margin-bottom: 0;
    line-height: 1.4;
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1rem;
  }

  .form-group {
    margin-bottom: 1rem;

    &.checkbox-group {
      grid-column: 1 / -1;
    }
  }

  .form-label {
    display: block;
    margin-bottom: 0.5rem;
    color: $black;
  }

  .error-message {
    color: $primary;
    font-size: 12px;
    margin-top: 0.25rem;
  }

  .help-text {
    font-size: 12px;
    margin-top: 0.25rem;
    margin-bottom: 0;
  }

  .form-check {
    margin-bottom: 0.5rem;
  }

  .goal-review {
    max-width: 600px;
    margin: 0 auto;
  }

  .review-card {
    background: #F8F9FA;
    border-radius: 12px;
    padding: 2rem;
  }

  .goal-summary {
    margin-bottom: 2rem;
  }

  .goal-header-review {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .goal-icon-large {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: $primary-light;
    display: flex;
    align-items: center;
    justify-content: center;

    .icon-image {
      width: 32px;
      height: 32px;
    }

    i {
      font-size: 24px;
      color: $primary;
    }
  }

  .goal-info-review h4 {
    margin-bottom: 0.25rem;
    color: $black;
  }

  .goal-description {
    color: $secondary;
    font-style: italic;
    margin-bottom: 0;
  }

  .goal-details-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }

  .detail-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem;
    background: $white;
    border-radius: 8px;

    .label {
      font-size: 14px;
      color: $secondary;
    }

    .value {
      font-size: 14px;
      font-weight: 600;
      color: $black;

      &.priority-high {
        color: $primary;
      }

      &.priority-medium {
        color: #FF9800;
      }

      &.priority-low {
        color: $success;
      }
    }
  }

  .wizard-actions {
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
    padding-top: 1rem;
    border-top: 1px solid #E0E0E0;

    .btn {
      min-width: 100px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }
  }
}

@media (max-width: 768px) {
  .goal-wizard {
    .goal-types-grid {
      grid-template-columns: 1fr;
    }

    .form-grid {
      grid-template-columns: 1fr;
    }

    .goal-details-grid {
      grid-template-columns: 1fr;
    }

    .wizard-actions {
      flex-direction: column;

      .btn {
        width: 100%;
      }
    }
  }
}
</style>
